import Products from 'merchandise-lib/constants/Product';
import { getOfferIdForSkuInProductConfigurations } from 'pricing-pages-lib/utils/offerUtils';
import { getCrmSuiteOwnershipLevel, getCurrentOwnedPaidCoreSeatsTotal, getHasProductInProductInventory, getHasProductVersionInProductInventory, getHasSkuInProductInventory, getProductVersionsFromProductInventory, getProductsFromProductInventoryQuantity } from 'pricing-pages-lib/utils/productInventoryUtils';
import { CoreSeatApiNameByPaidProductLevel, getBundledProductLinesForSkuId, getBundledProductsForSkuId, getCoreSeatSkuForProductLevel, getCrmSuiteProductLevelForProductConfigurations, getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly, getCurrentProductVersion, getCurrentSkuIdForProduct, getCurrentSuiteSkuIdsForProductLevel, getHighestProductLevelFromSkuIds, getHighestProductLevelSkuIdsByProductLine, getIncludedCoreSeatsQuantityForProductVersion, getIncludedSeatQuantityBySkuId, getIsCoreSeatSku, getIsPaidCoreSeatSku, getIsSmartCrmSku, getPackagedQuantitiesForQuantityPack, getProductForSkuId, getProductVersion, getQuantityPackDetailsListForProductVersion, getQuantityPackForProductApiName, getSku, getSkuIdIsAddonOrService, getSkuIdIsHub, getSkuIdIsInCrmSuite, getSkuUuidForSkuId, hasAnyTBSkuInProductData, isPaidSalesServiceSeat, isQuantifiableHub, isQuantityUpgrade, skuIdHasBundledProducts } from 'pricing-pages-lib/utils/productsUtils';
import { getIsStarterCoreSeatRequiredForSkuIds, getRequiredOnboardingSkuIdsWithRecommendedFallback } from 'pricing-pages-lib/utils/requiredOnboardingUtils';
import { NON_RECURRING, RECURRING } from 'self-service-api/constants/ChargeTypes';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { PRODUCT_VERSION, QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { isMarketingContactsSkuV2 } from 'self-service-api/core/utilities/isMarketingContactsSku';
import { getFilteredProductConfigurationsByChargeType } from 'ui-addon-upgrades/_core/utils/productUtils';
import { getIsUngatedForSeats } from './gatingUtils';
import { isQuantityPackDetails, isValidApiName, isValidPaidProductLevel, isValidQuantityPackApiName } from './typeguards';
export const createProductConfig = ({
  skuId,
  quantity = 1
}) => ({
  skuId,
  quantity
});
export const getCheckoutProductConfigurationsFromSkuId = (state, {
  productConfiguration,
  subtractOwnedUnitsIfBuyingNoAdditional = false,
  subtractOwnedSuiteProducts = false,
  subtractOwnedSeats = false,
  subtractOwnedBundles = false,
  includeOnboarding = true,
  includeOnboardingFallback = true,
  suiteProductLevel,
  allProductConfigurations
}) => {
  const {
    skuId,
    quantity
  } = productConfiguration;
  const returnConfigurations = [];
  let adjustedQuantity = quantity;
  const {
    productData: {
      skuMap
    }
  } = state;
  const sku = skuMap[skuId];
  const skuIdIsHub = getSkuIdIsHub(state, {
    skuId
  });
  const skuIdIsAddonOrService = getSkuIdIsAddonOrService(state, {
    skuId
  });
  const isHubQuantifiable = isQuantifiableHub(state, {
    skuId
  });
  const skuIdIsCoreSeat = getIsPaidCoreSeatSku(state, {
    skuId
  });
  const skuIdHasBundle = skuIdHasBundledProducts(state, {
    skuId
  });
  const getAdjustedConfiguration = (skuIdToAdjust, currentAdjustedQuantity) => {
    const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
      skuId: skuIdToAdjust
    });
    if (ownedQuantity === currentAdjustedQuantity) {
      returnConfigurations.push(createProductConfig({
        skuId: skuIdToAdjust,
        quantity: 0
      }));
      return returnConfigurations;
    }
    return null;
  };
  const getRequiredOnboardingConfigs = skuIdToCheck => {
    // Skus with productBasedOffers have to be included in a different
    // field when hitting offer-builder, so we don't include them here
    const requiredOnboardingSkuIds = getRequiredOnboardingSkuIdsWithRecommendedFallback(state, {
      skuId: skuIdToCheck,
      allSkuIds: allProductConfigurations.map(productConfig => productConfig.skuId),
      excludeFallback: !includeOnboardingFallback
    });
    if (requiredOnboardingSkuIds.length) {
      const requiredOnboardingConfigs = requiredOnboardingSkuIds.map(onboardingSkuId => createProductConfig({
        skuId: onboardingSkuId
      }));
      return requiredOnboardingConfigs;
    }
    return [];
  };
  if (skuIdHasBundle) {
    const ownsProductVersion = getHasProductVersionInProductInventory(state, {
      skuId
    });
    if (subtractOwnedBundles && ownsProductVersion) {
      const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
      if (adjustedConfiguration) return adjustedConfiguration;
    }
    if (!ownsProductVersion && includeOnboarding) {
      const requiredOnboardingConfigs = getRequiredOnboardingConfigs(skuId);
      returnConfigurations.push(...requiredOnboardingConfigs);
    }
  }
  if (sku.skuType === PRODUCT_VERSION && skuIdIsHub) {
    const ownsProductVersion = getHasProductVersionInProductInventory(state, {
      skuId
    });
    if (suiteProductLevel && ownsProductVersion && subtractOwnedSuiteProducts) {
      if (!isHubQuantifiable) {
        adjustedQuantity = 0;
      } else if (subtractOwnedSeats) {
        const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
        if (adjustedConfiguration) return adjustedConfiguration;
      }
    }
    const canIncludedOwnedProductForSuite = suiteProductLevel && getSkuIdIsInCrmSuite(state, {
      skuId,
      suiteProductLevel
    });
    if (!ownsProductVersion || canIncludedOwnedProductForSuite) {
      if (includeOnboarding) {
        const requiredOnboardingConfigs = getRequiredOnboardingConfigs(skuId);
        returnConfigurations.push(...requiredOnboardingConfigs);
      }
    } else if (skuIdIsHub) {
      if (isHubQuantifiable) {
        const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
        if (adjustedConfiguration) return adjustedConfiguration;
      } else {
        adjustedQuantity = 0;
      }
    }
  } else if (isQuantityPackDetails(sku)) {
    const {
      productLine
    } = getProductForSkuId(state, {
      skuId
    });
    if (productLine === MARKETING) {
      const isMarketingContactSku = isMarketingContactsSkuV2({
        skuMap,
        skuId
      });
      if (!isMarketingContactSku) {
        const {
          includedWithProductVersion
        } = sku;
        adjustedQuantity += includedWithProductVersion !== null && includedWithProductVersion !== void 0 ? includedWithProductVersion : 0;
      }
    }
    const {
      productVersionSkuId
    } = sku;
    const configsContainProductVersion = allProductConfigurations.some(({
      skuId: searchSkuId
    }) => searchSkuId === productVersionSkuId);
    if (subtractOwnedUnitsIfBuyingNoAdditional && !configsContainProductVersion) {
      const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
      if (adjustedConfiguration) return adjustedConfiguration;
    }
  } else if (skuIdIsAddonOrService) {
    const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
    if (adjustedConfiguration) return adjustedConfiguration;
  } else if (skuIdIsCoreSeat && subtractOwnedSeats) {
    const adjustedConfiguration = getAdjustedConfiguration(skuId, adjustedQuantity);
    if (adjustedConfiguration) return adjustedConfiguration;
  }
  const someConfigHasQuantity = allProductConfigurations.some(({
    quantity: tempQuantity
  }) => tempQuantity);
  if (adjustedQuantity > 0 || !someConfigHasQuantity) {
    returnConfigurations.push(createProductConfig({
      skuId,
      quantity: adjustedQuantity
    }));
  }
  return returnConfigurations;
};

// Filters out itemConfigs that have duplicated merchandiseIds.
// Intended to filter out duplicated HSFS for Startups as seen on this
// issue: https://zenhub.hubteam.com/app/workspaces/growth-cbx-5c2d3d676b243f914638ef39/issues/hubspot/pricing-pages-unified-ui/3251
export const getUniqueProductConfigurations = productConfigurations => productConfigurations.filter((config, index) => {
  return productConfigurations.map(c => c.skuId).indexOf(config.skuId) === index;
});
export const getCheckoutProductConfigurations = (state, {
  productConfigurations,
  subtractOwnedUnitsIfBuyingNoAdditional = false,
  subtractOwnedSuiteProducts = false,
  subtractOwnedSeats = false,
  subtractOwnedBundles = false,
  includeOnboarding = true,
  includeOnboardingFallback = true
}) => {
  const suiteProductLevel = getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  });
  let productConfigs = productConfigurations.flatMap(productConfiguration => getCheckoutProductConfigurationsFromSkuId(state, {
    productConfiguration,
    subtractOwnedUnitsIfBuyingNoAdditional,
    subtractOwnedSuiteProducts,
    subtractOwnedSeats,
    subtractOwnedBundles,
    includeOnboarding,
    includeOnboardingFallback,
    suiteProductLevel,
    allProductConfigurations: productConfigurations
  }));

  // The zero state for marketing calc when you own the base plan is a contacts config with quantity 0.
  // This is done so we still get the "Buy additional contacts" CTA even though nothing is really selected
  // But if we add an add-on, we want to remove the empty contacts config so offer id resolution knows
  // we are really just buying an addon
  const someCheckoutConfigHasQuantity = productConfigs.some(({
    quantity
  }) => quantity);
  productConfigs = productConfigs.filter(({
    quantity
  }) => quantity > 0 || !someCheckoutConfigHasQuantity);

  // If a one-time fee is a mandatory merch for more than one SKU of bundleSelections.
  // It will be added multiple times to itemConfigs.
  // The following operation assures such OTF appears only once in itemConfigs.
  return getUniqueProductConfigurations(productConfigs);
};

/**
 * Takes in a list of product configurations and filters it down to a list of non-recurring
 * product configurations configurations.
 */
export const getNonRecurringProductConfigurations = (state, {
  productConfigurations
}) => {
  const {
    productData: {
      rawProductList
    }
  } = state;
  return getFilteredProductConfigurationsByChargeType(
  // TODO [types migration]: create V2 with correct types before removing this cast
  rawProductList, productConfigurations, NON_RECURRING);
};

/**
 * Takes in a list of product configurations and query param term and splits them up into a
 * nested array of recurring product configurations that share the same offer.
 */
export const getOfferProductConfigurations = (state, {
  productConfigurations,
  queryParamTerm
}) => {
  const {
    productData: {
      rawProductList
    }
  } = state;
  const offerProductConfigurations = [];
  const recurringProductConfigurations = getFilteredProductConfigurationsByChargeType(
  // TODO [types migration]: create v2 in ui-addon-upgrades with correct types
  rawProductList, productConfigurations, RECURRING);
  recurringProductConfigurations.forEach(productConfiguration => {
    const offerIdForSkuId = getOfferIdForSkuInProductConfigurations(state, {
      allProductConfigurations: recurringProductConfigurations,
      productConfiguration,
      queryParamTerm
    });
    if (offerIdForSkuId) {
      offerProductConfigurations.push({
        offerId: offerIdForSkuId,
        productConfigurations: [productConfiguration]
      });
    }
  });
  const groupedOfferProductConfigurations = offerProductConfigurations.reduce((acc, offerProductConfiguration) => {
    const {
      offerId,
      productConfigurations: currentProductConfigurations
    } = offerProductConfiguration;
    const {
      productConfigurations: allProductConfigurationsForOfferId
    } = acc.find(({
      offerId: offerIdAdded
    }) => offerIdAdded === offerId) || {
      productConfigurations: []
    };
    const filteredAcc = acc.filter(({
      offerId: offerIdAdded
    }) => offerIdAdded !== offerId);
    return [...filteredAcc, {
      offerId,
      productConfigurations: [...allProductConfigurationsForOfferId, ...currentProductConfigurations]
    }];
  }, []);
  return groupedOfferProductConfigurations;
};

/**
 * Takes in a list of product configurations and query param term and splits them up into a
 * nested array of product configurations that share the same offer, including product based offers.
 */
export const getAllOfferProductConfigurations = (state, {
  productConfigurations,
  queryParamTerm
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const offerProductConfigurationsMap = productConfigurations.reduce((acc, productConfiguration) => {
    const offerIdForSkuId = getOfferIdForSkuInProductConfigurations(state, {
      allProductConfigurations: productConfigurations,
      productConfiguration,
      queryParamTerm
    });
    const {
      skuId,
      quantity
    } = productConfiguration;
    if (offerIdForSkuId) {
      const offerId = offerIdForSkuId;
      if (!acc.has(offerId)) {
        acc.set(offerId, {
          offerId,
          productConfigurations: []
        });
      }
      const skuInfo = skuMap[skuId];
      let checkoutQuantity = quantity;

      // adjust quantity pack quantity for checkout (E.g. 8000 contacts -> qty: 8)
      if (isQuantityPackDetails(skuInfo)) {
        const {
          includedWithQuantityPack
        } = skuInfo;
        if (!includedWithQuantityPack) {
          return acc;
        }
        checkoutQuantity = Math.ceil(quantity / includedWithQuantityPack);
      }
      acc.get(offerId).productConfigurations.push({
        skuId,
        quantity: checkoutQuantity
      });
    }
    return acc;
  }, new Map());
  return Array.from(offerProductConfigurationsMap.values());
};

/**
 * Sorts a list of product configurations to match the order of products
 * shown in the calculator. At time of writing it's sorted by: Marketing,
 * Sales, Service, CMS, Operations, Add-Ons, Services
 */
export const getSortedProductConfigurations = (state, {
  productConfigurations
}) => {
  const skuMap = state.productData.skuMap;
  const sorted = [...productConfigurations];
  sorted.sort((configA, configB) => {
    const productA = getProductForSkuId(state, {
      skuId: configA.skuId
    });
    const skuA = skuMap[configA.skuId];
    const bundledProductsA = getBundledProductsForSkuId(state, {
      skuId: configA.skuId
    });
    const productB = getProductForSkuId(state, {
      skuId: configB.skuId
    });
    const skuB = skuMap[configB.skuId];
    const bundledProductsB = getBundledProductsForSkuId(state, {
      skuId: configB.skuId
    });
    if (bundledProductsA.length && !bundledProductsB.length) {
      return -1;
    } else if (bundledProductsB.length && !bundledProductsA.length) {
      return 1;
    }
    if (productA.productLine && productA.productLevel) {
      if (!productB.productLevel || !productB.productLine) {
        // Product A is a hub or quantity pack, product B is an addon / service
        return -1;
      }
      if (productA.productLine === productB.productLine) {
        // Multiple skus in the same product line, sort quantity packs later
        if (skuA.skuType === PRODUCT_VERSION) {
          if (skuB.skuType === QUANTITY_PACK) {
            return -1;
          }
        } else if (skuA.skuType === QUANTITY_PACK) {
          if (skuB.skuType === PRODUCT_VERSION) {
            return 1;
          }
        }
      } else {
        // Different product lines, sort accordingly
        const orderedProductLines = [MARKETING, SALES, SERVICE, CMS, OPERATIONS];
        return orderedProductLines.indexOf(productA.productLine) - orderedProductLines.indexOf(productB.productLine);
      }
    } else if (productB.productLine && productB.productLevel) {
      // Product B is a hub or quantity pack, product A is an addon / service
      return 1;
    } else {
      // Since we don't have a predefined order for addons/services,
      // just sort them alphabetically by apiName
      if (productA.apiName && productB.apiName) {
        return productA.apiName.localeCompare(productB.apiName);
      }
    }
    return 0;
  });
  return sorted;
};
export const getSuiteProductConfigurations = (state, {
  productLevel
}) => {
  const suiteSkuIds = getCurrentSuiteSkuIdsForProductLevel(state, {
    productLevel
  });
  const productConfigs = suiteSkuIds.map(skuId => {
    const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
      skuId
    });
    return skuId && createProductConfig({
      skuId,
      quantity: ownedQuantity || 1
    });
  });
  return productConfigs.filter(config => !!config);
};

/**
 * A helper to get product configurations for a ProductVersion, optionally adding
 * additional units for the first quantity pack on the ProductVersion. If the ProductVersion
 * is owned, returns a config for 0 additional units.
 * @param state
 * @param skuId The skuId for the productVersion
 * @param additionalUnitQuantity The quantity of additional units to add
 */
export const getProductVersionConfigsWithUnits = (state, {
  skuId,
  additionalUnitSkuId,
  additionalUnitQuantity = 0
}) => {
  const isUngatedForSeats = getIsUngatedForSeats(state);
  const ownsProductVersion = getHasProductVersionInProductInventory(state, {
    skuId
  });
  const product = getProductForSkuId(state, {
    skuId
  });
  const productVersion = getCurrentProductVersion(state, {
    productId: product.id
  });
  const isQuantifiable = productVersion && productVersion.quantifiable;
  const isAdditionalUnitSkuIdDifferentFromSkuId = additionalUnitSkuId !== skuId;
  const productConfigurations = [];
  if (!ownsProductVersion) {
    productConfigurations.push(createProductConfig({
      skuId,
      quantity: 1
    }));
  }
  if (isUngatedForSeats && ownsProductVersion && isQuantifiable) {
    const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
      skuId
    });
    productConfigurations.push(createProductConfig({
      skuId,
      quantity: ownedQuantity || 1
    }));
  }
  if (additionalUnitSkuId && (additionalUnitQuantity || ownsProductVersion) && isAdditionalUnitSkuIdDifferentFromSkuId) {
    productConfigurations.push(createProductConfig({
      skuId: additionalUnitSkuId,
      quantity: additionalUnitQuantity
    }));
  }

  // For seats, the interstitial is expecting productConfigurations to not be empty.
  // productConfigurations will be empty for any owned products that have no quantity packs
  // and aren't quantifiable hubs.
  // To workaround this, we're adding the core seat to productConfigurations
  // which would be the only logical thing to buy on the interstitial in that situation anyway
  if (isUngatedForSeats && ownsProductVersion && !isQuantifiable && !additionalUnitSkuId && isValidPaidProductLevel(product.productLevel)) {
    const {
      skuId: coreSeatSkuId
    } = getCoreSeatSkuForProductLevel(state, {
      productLevel: product.productLevel
    });
    productConfigurations.push(createProductConfig({
      skuId: coreSeatSkuId,
      quantity: 0
    }));
  }
  return productConfigurations;
};
export const getProductsAndPacksForQueryParams = (state, {
  productConfigurations
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  if (!productConfigurations.length) {
    return {
      products: [],
      packs: []
    };
  }
  const tempProducts = [];
  const tempPacks = [];
  productConfigurations.forEach(({
    skuId,
    quantity
  }) => {
    const sku = skuMap[skuId];
    if (isQuantityPackDetails(sku)) {
      const {
        quantityPackApiName,
        productApiName
      } = sku;
      tempPacks.push(`${quantityPackApiName}_${productApiName}_${quantity}`);
    } else {
      const {
        apiName
      } = getProductForSkuId(state, {
        skuId
      });
      tempProducts.push(`${apiName}_${quantity}`);
    }
  });
  return {
    products: tempProducts,
    packs: tempPacks
  };
};

/**
 * With the introduction of standalone core seats, we also need to check the highest product level for core seats
 * rather than just using the highest product level for hub and bundle products.
 */
export const getProductLevelForCoreSeats = (state, {
  productConfigurations
}) => {
  const productInventorySkuIds = getProductVersionsFromProductInventory(state).map(({
    skuId
  }) => skuId);
  const productConfigurationsSkuIds = productConfigurations.map(({
    skuId
  }) => skuId);
  const searchSkuIds = [...new Set([...productInventorySkuIds, ...productConfigurationsSkuIds])].filter(skuId => getSkuIdIsHub(state, {
    skuId
  }) || skuIdHasBundledProducts(state, {
    skuId
  }) || getIsSmartCrmSku(state, {
    skuId
  }));
  const highestProductLevel = getHighestProductLevelFromSkuIds(state, {
    skuIds: searchSkuIds
  });
  const productLevelToUse = isValidPaidProductLevel(highestProductLevel) ? highestProductLevel : STARTER;
  return productLevelToUse;
};

/**
 * The included core seat quantity should total up the number of included core
 * seats you currently have or will have upon purchase.
 *
 * For SEATS only:
 * You get the free included core seat with your first purchase of a starter hub
 * You get to keep the free included core seat so long as you still own a starter hub
 *
 * For TB only:
 * For each Starter product, you get 1 included core seat, e.g. Marketing + CMS = 2,
 * but TB Starter = 1.
 *
 * To figure out the rest of the included seats you have, take the hubs you own and
 * the hubs you're about to purchase, take the highest tier hub per product line,
 * and sum up the included seats for those hubs.
 */
export const getIncludedCoreSeatsQuantity = (state, {
  productConfigurations
}) => {
  const selectedHubSkuIds = productConfigurations.filter(prodConfig => {
    const {
      skuId
    } = prodConfig;
    return getSkuIdIsHub(state, {
      skuId
    }) || skuIdHasBundledProducts(state, {
      skuId
    });
  }).map(({
    skuId
  }) => skuId);
  const ownedHubSkuIds = getProductVersionsFromProductInventory(state).filter(productVersion => getSkuIdIsHub(state, {
    skuId: productVersion.skuId
  }) || skuIdHasBundledProducts(state, {
    skuId: productVersion.skuId
  })).map(productVersion => productVersion.skuId);
  const highestHubsByProductLine = getHighestProductLevelSkuIdsByProductLine(state, {
    skuIds: [...selectedHubSkuIds, ...ownedHubSkuIds]
  });
  const useTBSeatCountLogic = hasAnyTBSkuInProductData(state);
  const hubSkuIds = [...new Set(Object.values(highestHubsByProductLine))];
  const willStillOwnANonTBStarterHub = !useTBSeatCountLogic && hubSkuIds.find(skuId => {
    const product = getProductForSkuId(state, {
      skuId
    });
    return product.productLevel === STARTER;
  });
  const allTBStarterOrProPlusHubSkuIds = hubSkuIds.filter(skuId => {
    const product = getProductForSkuId(state, {
      skuId
    });
    return product.productLevel !== STARTER || useTBSeatCountLogic;
  });
  let includedCoreSeatsQuantity = 0;
  if (willStillOwnANonTBStarterHub) {
    includedCoreSeatsQuantity = includedCoreSeatsQuantity + 1;
  }
  return allTBStarterOrProPlusHubSkuIds.reduce((total, hubSkuId) => {
    const includedCoreSeatsQuantityForSkuId = getIncludedCoreSeatsQuantityForProductVersion(state, {
      productVersionSkuId: hubSkuId
    });
    return total + includedCoreSeatsQuantityForSkuId;
  }, includedCoreSeatsQuantity);
};
export const getIncludedSalesServiceQuantityPackSeatTotal = (state, {
  productConfigurations
}) => {
  const selectedBundleConfig = productConfigurations.find(prodConfig => {
    const {
      skuId
    } = prodConfig;
    return skuIdHasBundledProducts(state, {
      skuId
    });
  });
  const selectedBundleSkuId = selectedBundleConfig && selectedBundleConfig.skuId;
  const ownedBundleProductVersion = getProductVersionsFromProductInventory(state).find(productVersion => skuIdHasBundledProducts(state, {
    skuId: productVersion.skuId
  }));
  const ownedBundleSkuId = ownedBundleProductVersion && ownedBundleProductVersion.skuId;
  const bundleSkuId = selectedBundleSkuId || ownedBundleSkuId;
  if (bundleSkuId) {
    const quantityPacks = getQuantityPackDetailsListForProductVersion(state, {
      productVersionSkuId: bundleSkuId
    });
    return quantityPacks && quantityPacks.reduce((acc, quantityPack) => {
      var _getPackagedQuantitie;
      const {
        skuId
      } = quantityPack;
      const isSalesServiceQuantityPackSeat = isPaidSalesServiceSeat(state, {
        skuId
      });
      if (!isSalesServiceQuantityPackSeat) {
        return acc;
      }
      return acc + ((_getPackagedQuantitie = getPackagedQuantitiesForQuantityPack(state, {
        skuId
      })) !== null && _getPackagedQuantitie !== void 0 ? _getPackagedQuantitie : 0);
    }, 0);
  }
  return 0;
};

/**
 * The purpose of this util is to return productConfigurations while considering
 * whether the configuration is currently, or no longer, eligible for the free
 * starter core seat.
 *
 * A portal should be eligible for the free starter core seat upon their first
 * purchase of a starter product. On subsequent starter purchases, they are
 * no longer eligible.
 *
 * A portal would also be no longer eligible if, for example on the Create a
 * Bundle page, they initially started off with viewing a starter product
 * configuration, but then selected all pro products instead.
 */
export const getProductConfigurationWithFreeCoreSeatIfPossible = (state, {
  productConfigurations
}) => {
  const skuIds = productConfigurations.map(config => config.skuId);
  const ownsIncludedStarterCoreSeat = getHasProductInProductInventory(state, {
    productAttributeFilters: {
      apiName: Products.CORE_SEATS_INCLUDED_STARTER
    }
  });
  const isStarterCoreSeatRequired = getIsStarterCoreSeatRequiredForSkuIds(state, {
    skuIds
  });
  const coreSeatsIncludedStarterSkuId = getCurrentSkuIdForProduct(state, {
    apiName: Products.CORE_SEATS_INCLUDED_STARTER
  });
  const foundConfig = productConfigurations.find(({
    skuId
  }) => skuId === coreSeatsIncludedStarterSkuId);
  const includedStarterCoreSeatQuantity = foundConfig ? foundConfig.quantity : 0;

  // remove if productConfigs have no starter
  if (!isStarterCoreSeatRequired && foundConfig) {
    return productConfigurations.filter(config => config.skuId !== coreSeatsIncludedStarterSkuId);
  }

  // checking for quantity > 1 because there should not be more than
  // 1 starter core seat included, which can be altered through
  // the query parameters
  if ((includedStarterCoreSeatQuantity === 0 || includedStarterCoreSeatQuantity > 1) && !ownsIncludedStarterCoreSeat && isStarterCoreSeatRequired) {
    const filteredConfigs = productConfigurations.filter(({
      skuId
    }) => skuId !== coreSeatsIncludedStarterSkuId);
    filteredConfigs.push(createProductConfig({
      skuId: coreSeatsIncludedStarterSkuId,
      quantity: 1
    }));
    return filteredConfigs;
  }
  return productConfigurations;
};

// Number of current owned + included core seats
// e.g. owns MhS: 4 additional + 1 free included = 5 total
export const getCurrentAdditionalAndIncludedCoreSeatTotal = state => {
  const currentOwnedCoreSeatsQuantity = getCurrentOwnedPaidCoreSeatsTotal(state);
  const currentIncludedCoreSeatsQuantity = getIncludedCoreSeatsQuantity(state, {
    productConfigurations: []
  });
  return currentOwnedCoreSeatsQuantity + currentIncludedCoreSeatsQuantity;
};

/*
* Returns the total number of core seats that should be the default starting
value on the interstitial (which is owned + additional)
Free -> Paid w/ only core seats should at a min be total users
Free -> Paid w/ other seats should be 0 (the other seat types will be updated)
Paid -> Paid maintain the existing number of core seats
*/
export const getInitialCoreSeatsTotalForProductConfiguration = (state, {
  productConfigurations,
  totalUsers
}) => {
  const productLevelForCoreSeats = getProductLevelForCoreSeats(state, {
    productConfigurations
  });
  const includedCoreSeatsQuantityAfterPurchase = getIncludedCoreSeatsQuantity(state, {
    productConfigurations
  });
  const apiNameForCoreSeats = CoreSeatApiNameByPaidProductLevel[productLevelForCoreSeats];
  const coreSeatSkuIdAfterPurchase = getCurrentSkuIdForProduct(state, {
    apiName: apiNameForCoreSeats
  }) || 0;
  const ownedCoreSeatsQuantityAfterPurchase = getProductsFromProductInventoryQuantity(state, {
    skuId: coreSeatSkuIdAfterPurchase
  });
  const ownedSkuIds = getProductVersionsFromProductInventory(state).map(product => product.skuId);
  const currentCoreSeatSkuId = ownedSkuIds.find(skuId => getIsPaidCoreSeatSku(state, {
    skuId
  }));
  const selectedSkuIds = productConfigurations.map(config => config.skuId);
  const hasOtherSeatTypes = [...ownedSkuIds, ...selectedSkuIds].find(skuId => isQuantifiableHub(state, {
    skuId
  }));
  const isFreeUser = ownedSkuIds.length === 0;
  const currentOwnedCoreSeatsQuantity = getCurrentOwnedPaidCoreSeatsTotal(state);

  // Number of core seats without any purchase made
  // e.g. owns MhS: 4 additional + 1 free included = 5 total
  const currentCoreSeatTotal = getCurrentAdditionalAndIncludedCoreSeatTotal(state);

  // Number of core seats that would be, after the current purchase
  // e.g. upgrade to MhP: 3 included = 3 total
  const coreSeatTotalAfterPurchase = ownedCoreSeatsQuantityAfterPurchase + includedCoreSeatsQuantityAfterPurchase;
  const baseReturn = {
    productLevelForCoreSeats,
    includedCoreSeatsQuantityAfterPurchase,
    ownedCoreSeatsQuantityAfterPurchase,
    currentOwnedCoreSeatsQuantity
  };
  return isFreeUser ? hasOtherSeatTypes ? Object.assign({}, baseReturn, {
    total: 0,
    updatedForUsers: false,
    updatedForOwnership: false
  }) : Object.assign({}, baseReturn, {
    total: totalUsers - includedCoreSeatsQuantityAfterPurchase,
    updatedForUsers: totalUsers > includedCoreSeatsQuantityAfterPurchase,
    updatedForOwnership: false
  }) : Object.assign({}, baseReturn, {
    total: currentCoreSeatTotal - includedCoreSeatsQuantityAfterPurchase,
    updatedForUsers: false,
    updatedForOwnership: currentCoreSeatSkuId !== coreSeatSkuIdAfterPurchase && currentCoreSeatTotal > coreSeatTotalAfterPurchase
  });
};
export const getInitialSalesServiceSeatsTotalForProductConfiguration = (state, {
  productConfigurations,
  skuId,
  totalUsers
}) => {
  const isHubQuantifiable = isQuantifiableHub(state, {
    skuId
  });
  if (!isHubQuantifiable) {
    return 0;
  }
  const ownedSkuIds = getProductVersionsFromProductInventory(state).map(product => product.skuId);
  const selectedSkuIds = productConfigurations.map(config => config.skuId);
  const hasOtherSeatTypes = [...ownedSkuIds, ...selectedSkuIds].find(searchSkuId => searchSkuId !== skuId && isQuantifiableHub(state, {
    skuId: searchSkuId
  }));
  const isFreeUser = ownedSkuIds.length === 0;
  return isFreeUser && !hasOtherSeatTypes ? totalUsers || 1 : 1;
};
const getProductConfigurationWithPaidCoreSeats = (state, {
  productConfigurations,
  totalUsers
}) => {
  const ownsAnyHubs = !!getProductVersionsFromProductInventory(state).find(productVersion => getSkuIdIsHub(state, {
    skuId: productVersion.skuId
  }) || skuIdHasBundledProducts(state, {
    skuId: productVersion.skuId
  }));
  const hasSelectedHub = productConfigurations.find(config => getSkuIdIsHub(state, {
    skuId: config.skuId
  }) || skuIdHasBundledProducts(state, {
    skuId: config.skuId
  }));
  const paidSeatsConfig = productConfigurations.find(config => getIsPaidCoreSeatSku(state, {
    skuId: config.skuId
  }));
  const shouldDisplayPaidCoreSeat = ownsAnyHubs || hasSelectedHub;
  if (!shouldDisplayPaidCoreSeat && !!paidSeatsConfig) {
    return productConfigurations.filter(config => !getIsPaidCoreSeatSku(state, {
      skuId: config.skuId
    }));
  }
  if (shouldDisplayPaidCoreSeat && !paidSeatsConfig) {
    const seatProductLevel = getProductLevelForCoreSeats(state, {
      productConfigurations
    });
    const {
      skuId: seatSkuId
    } = getCoreSeatSkuForProductLevel(state, {
      productLevel: seatProductLevel
    });
    const {
      total
    } = getInitialCoreSeatsTotalForProductConfiguration(state, {
      productConfigurations,
      totalUsers
    });
    productConfigurations.push(createProductConfig({
      skuId: seatSkuId,
      quantity: total || 0
    }));
  }
  return productConfigurations;
};
export const getInitialProductConfigurationsWithSalesServiceSeats = (state, {
  productConfigurations,
  totalUsers
}) => {
  const isUngatedForSeats = getIsUngatedForSeats(state);
  if (!isUngatedForSeats) {
    return productConfigurations;
  }
  return productConfigurations.map(productConfig => {
    const skuId = productConfig.skuId;
    const isHubQuantifiable = isQuantifiableHub(state, {
      skuId
    });
    if (!isHubQuantifiable) {
      return productConfig;
    }
    const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
      skuId
    });
    const initialTotal = getInitialSalesServiceSeatsTotalForProductConfiguration(state, {
      productConfigurations,
      skuId,
      totalUsers
    });
    const selectedTotal = productConfig.quantity;
    const quantity = Math.max(ownedQuantity, initialTotal, selectedTotal, 1);
    return createProductConfig({
      skuId,
      quantity
    });
  });
};
export const getProductConfigurationForSeats = (state, {
  productConfigurations,
  totalUsers
}) => {
  const isUngatedForSeats = getIsUngatedForSeats(state);
  if (!isUngatedForSeats) {
    return productConfigurations;
  }
  const configsWithFreeSeat = getProductConfigurationWithFreeCoreSeatIfPossible(state, {
    productConfigurations
  });
  const configsWithPaidSeats = getProductConfigurationWithPaidCoreSeats(state, {
    productConfigurations: configsWithFreeSeat,
    totalUsers
  });
  const configsWithSalesServiceSeats = getInitialProductConfigurationsWithSalesServiceSeats(state, {
    productConfigurations: configsWithPaidSeats,
    totalUsers
  });
  return configsWithSalesServiceSeats;
};
export const getProductConfigsContainBundle = (state, {
  productConfigurations
}) => {
  return productConfigurations.some(({
    skuId
  }) => skuIdHasBundledProducts(state, {
    skuId
  }));
};
const productConfigurationsWithProductsForUnownedQuantityPacks = (state, {
  productConfigurations
}) => {
  return productConfigurations.flatMap(config => {
    const skuId = config.skuId;
    const sku = getSku(state, {
      skuId
    });
    const ownsQuantityPack = getHasSkuInProductInventory(state, {
      skuId
    });
    const productVersion = getProductVersion(state, {
      skuId
    });
    const configsContainProductVersion = productConfigurations.find(productConfiguration => productConfiguration.skuId === (productVersion === null || productVersion === void 0 ? void 0 : productVersion.skuId));
    const parentHasBundles = !!productVersion && skuIdHasBundledProducts(state, {
      skuId: productVersion === null || productVersion === void 0 ? void 0 : productVersion.skuId
    });
    if (isQuantityPackDetails(sku) && !ownsQuantityPack && !configsContainProductVersion && !parentHasBundles) {
      const productConfig = createProductConfig({
        skuId: sku.productVersionSkuId
      });
      return [config, productConfig];
    }
    return config;
  });
};

/*
  In the case where you own suite or Marketing Hub, and you're attempting to
  checkout a mixed touchless purchase (quantity increase + upgrade) with
  marketing contacts, marketing contacts cannot go to upgrade-catalog without
  also including the hub. To ensure the mixed touchless purchase motion logic is working
  correctly, you need to send in the entire suite or hub so it calculates the correct
  offer for Marketing Hub instead (suite or a la carte, or discretionary discounts)
  Without this, you will get a TTS purchase motion because the a la carte discount
  (e.g. 10% annual) for Marketing Hub cannot beat your exiting suite discount.
  So if it's a mixed touchless purchase motion, we want all the product configs
  to go to checkout. In the case of all quantity increases, or all net new
  purchases, we can filter down to just those configs
   */
export const getCheckoutProductConfigurationsForSeats = (state, {
  productConfigurations,
  includeOnboarding = true,
  includeOnboardingFallback = true
}) => {
  const isUngatedForSeats = getIsUngatedForSeats(state);
  const productConfigsContainsBundle = getProductConfigsContainBundle(state, {
    productConfigurations
  });
  if (!isUngatedForSeats && !productConfigsContainsBundle) {
    return productConfigurations;
  }
  const filteredConfigs = productConfigurations.filter(config => config.quantity > 0);
  const suiteProductLevel = getCrmSuiteProductLevelForProductConfigurations(state, {
    productConfigurations: filteredConfigs
  });
  const selectedOwnedSuiteProductLevel = getCrmSuiteOwnershipLevel(state) === suiteProductLevel;
  const initialCheckoutConfigs = getCheckoutProductConfigurations(state, {
    productConfigurations: filteredConfigs,
    subtractOwnedUnitsIfBuyingNoAdditional: true,
    subtractOwnedSuiteProducts: selectedOwnedSuiteProductLevel,
    subtractOwnedBundles: true,
    subtractOwnedSeats: true,
    includeOnboarding,
    includeOnboardingFallback
  });
  const isCheckingOutSomething = initialCheckoutConfigs.find(config => config.quantity > 0);
  const isAllQuantityUpgrade = isQuantityUpgrade(state, {
    productConfigurations: initialCheckoutConfigs
  });
  const isAllNetNew = initialCheckoutConfigs.every(config => !isQuantityUpgrade(state, {
    productConfigurations: [createProductConfig({
      skuId: config.skuId
    })]
  }));
  if (isAllQuantityUpgrade || isAllNetNew || !isCheckingOutSomething) {
    return initialCheckoutConfigs;
  }
  return productConfigurationsWithProductsForUnownedQuantityPacks(state, {
    productConfigurations: filteredConfigs
  });
};
export const getAdditionalAndIncludedCoreSalesServiceSeatsTotalInProductConfiguration = (state, {
  productConfigurations
}) => {
  const totalAdditionalCoreSalesServiceSeats = productConfigurations.reduce((acc, config) => {
    const isPaidCoreSeat = getIsPaidCoreSeatSku(state, {
      skuId: config.skuId
    });
    const isSalesServiceHubSeat = isQuantifiableHub(state, {
      skuId: config.skuId
    });
    const isSalesServiceQuantityPackSeat = isPaidSalesServiceSeat(state, {
      skuId: config.skuId
    });
    return isPaidCoreSeat || isSalesServiceHubSeat || isSalesServiceQuantityPackSeat ? acc + config.quantity : acc;
  }, 0);
  const totalIncludedCoreSeats = getIncludedCoreSeatsQuantity(state, {
    productConfigurations
  });

  // We only need the included sales/service seats for QuantityPacks.
  // Sales/Service hub technically "include" a sales/service seat, but since
  // buying the "hub" is actually buying the seat, adding that would double count the seat
  const totalIncludedSalesServiceQuantityPackSeats = getIncludedSalesServiceQuantityPackSeatTotal(state, {
    productConfigurations
  });
  const totalSeats = totalAdditionalCoreSalesServiceSeats + totalIncludedCoreSeats + totalIncludedSalesServiceQuantityPackSeats;
  return totalSeats;
};

// Adds a sku id to productConfigurations while factoring in ownership
export const getProductConfigurationsWithAddedSkuId = (state, {
  productConfigurations,
  skuId
}) => {
  const updatedConfigs = [...productConfigurations];
  const startingCoreSeatLevel = getProductLevelForCoreSeats(state, {
    productConfigurations
  });
  if (skuId && (!getHasProductVersionInProductInventory(state, {
    skuId
  }) || isQuantifiableHub(state, {
    skuId
  }))) {
    const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
      skuId
    });
    updatedConfigs.push(createProductConfig({
      skuId,
      quantity: ownedQuantity || 1
    }));
  }
  const newCoreSeatLevel = getProductLevelForCoreSeats(state, {
    productConfigurations: updatedConfigs
  });
  if (startingCoreSeatLevel !== newCoreSeatLevel) {
    return updatedConfigs.filter(config => {
      return !getIsCoreSeatSku(state, {
        skuId: config.skuId
      });
    });
  }
  return updatedConfigs;
};
export const isBundlingProductConfigurationsWithSkuId = (state, {
  productConfigurations,
  skuId
}) => {
  const beforeSkuIds = productConfigurations.map(config => config.skuId);
  const afterSkuIds = [skuId, ...beforeSkuIds];
  const beforeSuiteProductLevel = getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly(state, {
    skuIds: beforeSkuIds
  });
  const afterSuiteProductLevel = getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly(state, {
    skuIds: afterSkuIds
  });
  if (!beforeSuiteProductLevel && afterSuiteProductLevel) {
    const suiteProductConfigs = getSuiteProductConfigurations(state, {
      productLevel: afterSuiteProductLevel
    });
    const bundleConfig = suiteProductConfigs.find(config => skuIdHasBundledProducts(state, {
      skuId: config.skuId
    }));
    return !!bundleConfig;
  }
  return false;
};
export const getBundledSuiteProductConfigurationsForSkuId = (state, {
  productConfigurations,
  skuId
}) => {
  const isBundling = isBundlingProductConfigurationsWithSkuId(state, {
    productConfigurations,
    skuId
  });
  if (!isBundling) {
    return getProductConfigurationsWithAddedSkuId(state, {
      productConfigurations,
      skuId
    });
  }
  const skuIds = [skuId, ...productConfigurations.map(config => config.skuId)];
  const suiteProductLevel = getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly(state, {
    skuIds
  });
  const suiteProductConfigs = getSuiteProductConfigurations(state, {
    productLevel: suiteProductLevel
  });
  const bundleConfig = suiteProductConfigs.find(config => skuIdHasBundledProducts(state, {
    skuId: config.skuId
  }));
  const bundleSkuId = bundleConfig.skuId;
  const bundledProductLines = getBundledProductLinesForSkuId(state, {
    skuId: bundleSkuId
  });

  // Remove all a la carte hub skus covered by bundle sku
  const filteredConfig = productConfigurations.filter(config => {
    const product = getProductForSkuId(state, {
      skuId: config.skuId
    });
    return !product.productLine || !bundledProductLines.includes(product.productLine);
  });
  filteredConfig.push(createProductConfig({
    skuId: bundleSkuId
  }));
  return filteredConfig;
};

/**
 * For use in PlanBuilder.
 *
 * Removes a product line from the current product configurations.
 * If the product line is part of a bundle, this will unbundle the
 * configuration and retain all other products as individual items.
 */
export const getProductConfigurationsWithoutProductLine = (state, {
  productConfigurations,
  productLine
}) => {
  const bundleConfig = productConfigurations.find(config => skuIdHasBundledProducts(state, {
    skuId: config.skuId
  }));
  if (bundleConfig && bundleConfig.skuId) {
    // get the list of bundled products
    const bundledProducts = getBundledProductsForSkuId(state, {
      skuId: bundleConfig.skuId
    });

    // Check if the product line is bundled in the current bundle
    const isProductLineBundled = bundledProducts.some(product => (product === null || product === void 0 ? void 0 : product.productLine) === productLine);

    // Only unbundle if the product line we're removing is part of the bundle
    if (isProductLineBundled) {
      const bundleQuantityPacks = getQuantityPackDetailsListForProductVersion(state, {
        productVersionSkuId: bundleConfig.skuId
      });

      // remove the bundle sku and quantity packs associated with the bundle from productConfigurations
      const filteredConfigs = productConfigurations.filter(config => ![...bundleQuantityPacks.map(qp => qp.skuId), bundleConfig.skuId].includes(config.skuId));

      // Add in all the other a la carte skus from the bundle except this productLine
      bundledProducts.forEach(product => {
        if (product !== null && product !== void 0 && product.productLine && product.productLine !== productLine) {
          const currentProductVersion = getCurrentProductVersion(state, {
            productId: product.id
          });
          if (currentProductVersion) {
            var _productConfiguration;
            filteredConfigs.push((_productConfiguration = productConfigurations.find(config => config.skuId === currentProductVersion.skuId)) !== null && _productConfiguration !== void 0 ? _productConfiguration : createProductConfig({
              skuId: currentProductVersion.skuId
            }));
          }
        }
      });
      return filteredConfigs;
    }
  }

  // if not unbundling, remove any product from productLine
  const filteredConfigs = productConfigurations.filter(config => {
    const product = getProductForSkuId(state, {
      skuId: config.skuId
    });
    const isCoreSeat = getIsCoreSeatSku(state, {
      skuId: config.skuId
    });
    const sku = getSku(state, {
      skuId: config.skuId
    });
    if (isQuantityPackDetails(sku)) {
      const productVersionSkuId = sku.productVersionSkuId;
      const productVersionProduct = getProductForSkuId(state, {
        skuId: productVersionSkuId
      });

      // Only filter out quantity packs that belong to the product line being removed
      return productVersionProduct.productLine !== productLine;
    }
    return !isCoreSeat && product.productLine !== productLine;
  });
  return filteredConfigs;
};
export const getIncludedSeatQuantityForProductConfiguration = (state, {
  productConfigurations,
  seatUnitName
}) => {
  return productConfigurations.reduce((acc, productConfig) => {
    const included = getIncludedSeatQuantityBySkuId(state, {
      skuId: productConfig.skuId,
      seatUnitName
    });
    return acc + included;
  }, 0);
};

// Returns all productConfigurations with their owned quantities removed
// This is NOT intended for checkout, only for the MRR check for required
// onboarding
export const getAdditionalProductConfigurations = (state, {
  productConfigurations
}) => {
  return productConfigurations.reduce((acc, productConfig) => {
    const {
      skuId,
      quantity
    } = productConfig;
    const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
      skuId,
      calculateIncludedWithQuantityPack: false
    });
    const additional = quantity - ownedQuantity;

    // For unowned, include the unchanged productConfig
    if (!ownedQuantity) {
      acc.push(productConfig);
      return acc;
    }
    // For owned, with no additional quantities, remove
    if (!additional) {
      return acc;
    }
    // For owned with additional quantities, adjust
    acc.push(createProductConfig({
      skuId,
      quantity: additional
    }));
    return acc;
  }, []);
};
export const convertSkuIdsToSkuUuidsForProductConfigurations = (state, {
  productConfigurations
}) => {
  const convertedProductConfigurations = productConfigurations.map(productConfig => {
    const skuUuid = getSkuUuidForSkuId(state, {
      skuId: productConfig.skuId
    });
    return {
      quantity: productConfig.quantity,
      skuUuid
    };
  });
  return convertedProductConfigurations;
};
export const productConfigurationsToBundleProducts = (state, {
  productConfigurations
}) => {
  const bundleProductsMap = new Map();

  // populate the map with the product configurations
  productConfigurations.forEach(({
    skuId,
    quantity
  }) => {
    const {
      apiName
    } = getProductForSkuId(state, {
      skuId
    });
    const sku = getSku(state, {
      skuId
    });
    if (isQuantityPackDetails(sku)) {
      var _bundleProductsMap$ge;
      bundleProductsMap.set(apiName, {
        productApiName: apiName,
        quantity: ((_bundleProductsMap$ge = bundleProductsMap.get(apiName)) === null || _bundleProductsMap$ge === void 0 ? void 0 : _bundleProductsMap$ge.quantity) || 1,
        quantityPacks: [{
          quantityPackApiName: sku.quantityPackApiName,
          quantity: quantity || 1
        }]
      });
    } else {
      var _bundleProductsMap$ge2;
      bundleProductsMap.set(apiName, {
        productApiName: apiName,
        quantity: quantity || 1,
        quantityPacks: ((_bundleProductsMap$ge2 = bundleProductsMap.get(apiName)) === null || _bundleProductsMap$ge2 === void 0 ? void 0 : _bundleProductsMap$ge2.quantityPacks) || []
      });
    }
  });
  const bundleProducts = Array.from(bundleProductsMap.values());
  return bundleProducts;
};
export const bundleProductsToProductConfigurations = (state, {
  bundledProducts
}) => {
  const quantityPacksConfigs = [];
  const productConfigurations = [];
  bundledProducts.map(({
    productApiName,
    quantity,
    quantityPacks
  }) => {
    const productVersion = isValidApiName(productApiName) ? getCurrentProductVersion(state, {
      apiName: productApiName
    }) : null;
    if ((quantityPacks === null || quantityPacks === void 0 ? void 0 : quantityPacks.length) > 0) {
      quantityPacks.forEach(({
        quantityPackApiName,
        quantity: quantityPackQuantity
      }) => {
        if (!isValidApiName(productApiName) || !isValidQuantityPackApiName(quantityPackApiName)) {
          return;
        }
        const quantityPack = getQuantityPackForProductApiName(state, {
          productApiName,
          quantityPackApiName
        });
        if (quantityPack && quantityPack.skuId) {
          quantityPacksConfigs.push(createProductConfig({
            skuId: quantityPack.skuId,
            quantity: quantityPackQuantity
          }));
        }
      });
    }
    if (productVersion && productVersion.skuId) {
      productConfigurations.push(createProductConfig({
        skuId: productVersion.skuId,
        quantity
      }));
    }
    return null;
  });
  return [...productConfigurations, ...quantityPacksConfigs];
};